import { useEffect, useState, FormEvent, useCallback } from "react";
import { PlayerRole, type Role } from "../shared/constants";
import { API_BASE_URL } from "../utils/api";

interface Game {
  game_name: string;
}

interface Team {
  team_name: string;
}

interface PlayerLoginProps {
  onLogin: (loginData: {
    playerId: string;
    gameName: string;
    teamName: string;
    teamCodeName: string;
    role: Role;
    loginCode: string;
    communicationLevel: number | null;
    scanningLevel: number | null;
    spaceshipDamage: number | null;
    personalScore: number;
    accessToken: string;
    refreshToken: string;
  }) => void;
  onBack: () => void;
}

export default function PlayerLogin({ onLogin, onBack }: PlayerLoginProps) {
  const [games, setGames] = useState<Game[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedGame, setSelectedGame] = useState<string>("");
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [role, setRole] = useState<Role>("" as Role);
  const [loginCode, setLoginCode] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Cache utilities for games list
  const GAME_LIST_CACHE_KEY = "lost-in-space-game-list";
  const GAME_LIST_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  const getCachedGameList = useCallback((): Game[] | null => {
    try {
      const cached = localStorage.getItem(GAME_LIST_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp > GAME_LIST_CACHE_TTL) {
        localStorage.removeItem(GAME_LIST_CACHE_KEY);
        return null;
      }

      return data as Game[];
    } catch (err) {
      console.error("Error reading game list cache:", err);
      localStorage.removeItem(GAME_LIST_CACHE_KEY);
      return null;
    }
  }, [GAME_LIST_CACHE_TTL]);

  const setCachedGameList = useCallback((games: Game[]) => {
    try {
      const cacheData = {
        data: games,
        timestamp: Date.now()
      };
      localStorage.setItem(GAME_LIST_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing game list cache:", err);
    }
  }, []);

  // Fetch all games (with caching)
  useEffect(() => {
    let cancelled = false;

    // Check cache first
    const cachedGames = getCachedGameList();
    if (cachedGames && cachedGames.length > 0) {
      console.log("Using cached game list in PlayerLogin");
      setGames(cachedGames);
      // Still fetch in background to update cache
    }

    // Always fetch from API to ensure we have the latest games
    fetch(`${API_BASE_URL}/games/`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch games: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const gamesList = Array.isArray(data) ? data : [];
        setGames(gamesList);
        // Update cache
        setCachedGameList(gamesList);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("Failed to fetch games:", err);
        // Only set empty array if we don't have cached games
        if (!cachedGames || cachedGames.length === 0) {
          setGames([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getCachedGameList, setCachedGameList]);

  // Cache utilities for teams list (per game)
  const TEAMS_CACHE_KEY = (gameName: string) => `lost-in-space-teams-${gameName}`;
  const TEAMS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds (same as games list)

  const getCachedTeams = useCallback((gameName: string): Team[] | null => {
    if (!gameName) return null;
    try {
      const cached = localStorage.getItem(TEAMS_CACHE_KEY(gameName));
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp > TEAMS_CACHE_TTL) {
        localStorage.removeItem(TEAMS_CACHE_KEY(gameName));
        return null;
      }

      return data as Team[];
    } catch (err) {
      console.error("Error reading teams cache:", err);
      localStorage.removeItem(TEAMS_CACHE_KEY(gameName));
      return null;
    }
  }, [TEAMS_CACHE_TTL]);

  const setCachedTeams = useCallback((gameName: string, teams: Team[]) => {
    if (!gameName) return;
    try {
      const cacheData = {
        data: teams,
        timestamp: Date.now()
      };
      localStorage.setItem(TEAMS_CACHE_KEY(gameName), JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing teams cache:", err);
    }
  }, []);

  // Fetch teams for selected game (with caching)
  useEffect(() => {
    if (!selectedGame) {
      setTeams([]);
      setSelectedTeam("");
      return;
    }

    // Check cache first
    const cachedTeams = getCachedTeams(selectedGame);
    if (cachedTeams) {
      console.log("Using cached teams list for game:", selectedGame);
      setTeams(cachedTeams);
      return;
    }

    // Fetch from API if cache is empty or expired
    fetch(`${API_BASE_URL}/teams/by-game-name?game_name=${encodeURIComponent(selectedGame)}`)
      .then(res => res.json())
      .then((data) => {
        const teamsList = Array.isArray(data) ? data : [];
        setTeams(teamsList);
        // Update cache
        setCachedTeams(selectedGame, teamsList);
      })
      .catch(err => {
        console.error("Failed to fetch teams:", err);
        setTeams([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame, getCachedTeams, setCachedTeams]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedGame || !selectedTeam || !role || !loginCode) {
      setError("Please fill all fields");
      return;
    }

    // Call login API
    fetch(`${API_BASE_URL}/players/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        team_name: selectedTeam,
        role,
        login_code: loginCode
      })
    })
      .then(res => {
        if (!res.ok) throw new Error("Invalid credentials");
        return res.json();
      })
      .then((data) => {
        onLogin({
          playerId: data.player_id,
          gameName: data.game_name || selectedGame,
          teamName: data.team_name || selectedTeam,
          teamCodeName: data.team_code_name || "",
          role: data.role as Role,
          loginCode: data.login_code,
          communicationLevel: data.comm_lvl || null,
          scanningLevel: data.scan_lvl || null,
          spaceshipDamage: (data.ship_dmg ?? data.spaceship_damage) ?? null,
          personalScore: data.personal_score ?? 0,
          accessToken: data.access_token,
          refreshToken: data.refresh_token
        });
      })
      .catch(err => setError(err.message));
  };

  return (
    <div style={{ 
      padding: "2rem", 
      backgroundColor: "#f8f9fa", 
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <div style={{
        width: "100%",
        maxWidth: "500px"
      }}>
        {/* Header */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "1rem", 
          marginBottom: "2rem" 
        }}>
          <button 
            onClick={onBack}
            style={{ 
              padding: "0.5rem 1rem", 
              cursor: "pointer",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "bold"
            }}
          >
            ← Back
          </button>
          <h1 style={{ margin: 0, color: "#212529" }}>Player Login</h1>
        </div>

        {/* Login Card */}
        <div style={{
          padding: "2rem",
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontWeight: "bold", color: "#212529", fontSize: "0.95rem" }}>
                Game
              </label>
              <select 
                value={selectedGame} 
                onChange={e => setSelectedGame(e.target.value)}
                data-testid="player-login-game-select"
                style={{ 
                  width: "100%", 
                  padding: "0.75rem",
                  fontSize: "0.95rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  backgroundColor: "white",
                  cursor: "pointer"
                }}
              >
                <option value="">-- Select a game --</option>
                {games.map(game => (
                  <option key={game.game_name} value={game.game_name}>
                    {game.game_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontWeight: "bold", color: "#212529", fontSize: "0.95rem" }}>
                Team
              </label>
              <select 
                value={selectedTeam} 
                onChange={e => setSelectedTeam(e.target.value)} 
                disabled={!selectedGame}
                data-testid="player-login-team-select"
                style={{ 
                  width: "100%", 
                  padding: "0.75rem",
                  fontSize: "0.95rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  backgroundColor: !selectedGame ? "#f8f9fa" : "white",
                  cursor: !selectedGame ? "not-allowed" : "pointer",
                  opacity: !selectedGame ? 0.6 : 1
                }}
              >
                <option value="">-- Select a team --</option>
                {teams.map(team => (
                  <option key={team.team_name} value={team.team_name}>
                    {team.team_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontWeight: "bold", color: "#212529", fontSize: "0.95rem" }}>
                Role
              </label>
              <select 
                value={role} 
                onChange={e => setRole(e.target.value as Role)}
                data-testid="player-login-role-select"
                style={{ 
                  width: "100%", 
                  padding: "0.75rem",
                  fontSize: "0.95rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  backgroundColor: "white",
                  cursor: "pointer"
                }}
              >
                <option value="">-- Select a role --</option>
                <option value={PlayerRole.AGENCY}>Agency</option>
                <option value={PlayerRole.SPACESHIP}>Spaceship</option>
              </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <label style={{ fontWeight: "bold", color: "#212529", fontSize: "0.95rem" }}>
                Login Code
              </label>
              <input 
                type="text" 
                value={loginCode} 
                onChange={e => setLoginCode(e.target.value)}
                placeholder="Enter your login code"
                data-testid="player-login-code-input"
                style={{ 
                  width: "94%", 
                  padding: "0.75rem",
                  fontSize: "0.95rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                  backgroundColor: "white",
                  cursor: "text"
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: "0.75rem",
                backgroundColor: "#f8d7da",
                color: "#721c24",
                borderRadius: "4px",
                border: "1px solid #f5c6cb",
                fontSize: "0.875rem"
              }}>
                {error}
              </div>
            )}

            <button 
              type="submit"
              style={{ 
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                cursor: "pointer",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontWeight: "bold"
              }}
            >
              Login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

